import { isSafeId, qualityDocumentPath } from './deep-link';

export const SCRIVE_SIGN_PENDING_KEY = 'scriveSignPending';
export const SCRIVE_SIGNING_SESSION_KEY = 'scriveSigningSession';
export const LOGIN_RETURN_PATH_KEY = 'scriveLoginReturnPath';
export const SIGNING_SESSION_TTL_MS = 15 * 60 * 1000;

export interface PendingSign {
  scriveDocumentId: string;
  qualityDocumentId: string;
  projectId?: string;
  fields?: string[];
}

export interface SigningSession {
  authId: string;
  userId: string;
  projectId: string;
  qualityDocumentId: string;
  expiresAt: number;
}

function readJson(storage: Storage, key: string) {
  try {
    const value = storage.getItem(key);
    if (!value) {
      return null;
    }

    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

export function getPendingSign(): PendingSign | null {
  return readJson(localStorage, SCRIVE_SIGN_PENDING_KEY);
}

export function clearPendingSign() {
  try {
    localStorage.removeItem(SCRIVE_SIGN_PENDING_KEY);
  } catch (e) {
    return;
  }
}

export function storeSigningSession(projectId: string, qualityDocumentId: string) {
  const authId = sessionStorage.getItem('authId');
  const userId = sessionStorage.getItem('userId') || '';

  if (!authId || !isSafeId(projectId) || !isSafeId(qualityDocumentId)) {
    return;
  }

  const session: SigningSession = {
    authId,
    userId,
    projectId,
    qualityDocumentId,
    expiresAt: Date.now() + SIGNING_SESSION_TTL_MS,
  };

  try {
    localStorage.setItem(SCRIVE_SIGNING_SESSION_KEY, JSON.stringify(session));
  } catch (e) {
    return;
  }
}

export function clearSigningSession() {
  try {
    localStorage.removeItem(SCRIVE_SIGNING_SESSION_KEY);
  } catch (e) {
    return;
  }
}

export function getStoredSigningSession(): SigningSession | null {
  const session = readJson(localStorage, SCRIVE_SIGNING_SESSION_KEY);
  if (!session || !session.authId || !session.projectId || !session.qualityDocumentId) {
    return null;
  }

  if (Date.now() > session.expiresAt) {
    clearSigningSession();
    return null;
  }

  return session;
}

export function restoreSigningSessionIfMatchingRoute(
  params?: { id?: string; documentId?: string },
  pathname?: string,
) {
  const session = getStoredSigningSession();
  if (!session) {
    return false;
  }

  const routeProjectId = params && params.id;
  const routeDocumentId = params && params.documentId;
  const pathMatches = typeof pathname === 'string'
    && pathname.includes(`/qualitydocument/${session.qualityDocumentId}`);
  const paramsMatch = typeof routeProjectId === 'string'
    && typeof routeDocumentId === 'string'
    && isSafeId(routeProjectId)
    && isSafeId(routeDocumentId)
    && routeProjectId === session.projectId
    && routeDocumentId === session.qualityDocumentId;

  if (!pathMatches && !paramsMatch) {
    return false;
  }

  if (!sessionStorage.getItem('authId')) {
    sessionStorage.setItem('authId', session.authId);
    if (session.userId) {
      sessionStorage.setItem('userId', session.userId);
    }
  }

  clearSigningSession();
  return true;
}

export function qualityDocumentPathFromPending(pending?: PendingSign | null) {
  const projectId = pending && pending.projectId;
  const documentId = pending && pending.qualityDocumentId;

  if (typeof projectId !== 'string' || typeof documentId !== 'string' || !isSafeId(projectId) || !isSafeId(documentId)) {
    return null;
  }

  return qualityDocumentPath(projectId, documentId);
}

export function rememberLoginReturnPath(pathname?: string) {
  const pendingPath = qualityDocumentPathFromPending(getPendingSign());
  const path = pendingPath
    || (typeof pathname === 'string' && pathname.includes('/qualitydocument/') ? pathname : null);

  if (!path || path.indexOf('/projects/') !== 0) {
    return;
  }

  try {
    localStorage.setItem(LOGIN_RETURN_PATH_KEY, path);
  } catch (e) {
    return;
  }
}

export function clearLoginReturnPath() {
  try {
    localStorage.removeItem(LOGIN_RETURN_PATH_KEY);
  } catch (e) {
    return;
  }
}

export function consumeLoginReturnPath() {
  try {
    const path = localStorage.getItem(LOGIN_RETURN_PATH_KEY);
    localStorage.removeItem(LOGIN_RETURN_PATH_KEY);

    if (!path || path.indexOf('/projects/') !== 0 || path.indexOf('/qualitydocument/') === -1) {
      return null;
    }

    return path;
  } catch (e) {
    return null;
  }
}
